import { Injectable } from '@angular/core';
import {
  FunctionInterface,
  FunctionParameterInterface,
  FunctionParameterParameterLocation,
  FunctionHeaderInterface,
} from '@vendasta/ai-assistants';
import {
  CurlImportArrayItem,
  CurlImportObject,
  CurlImportResult,
} from '../../components/ai-function-form/curl-import-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class AiToolCurlService {
  /**
   * Converts a CurlImportResult to a FunctionInterface
   */
  curlCommandToFunction(curlImport: CurlImportResult, formId?: string, formDescription?: string): FunctionInterface {
    // Prepare function parameters from body and query params
    const functionParameters: FunctionParameterInterface[] = [
      ...curlImport.bodyParams.map((param) =>
        this.convertCurlImportObjectToFunctionParameter(param, false, FunctionParameterParameterLocation.LOCATION_BODY),
      ),

      ...curlImport.queryParams.map(
        (param): FunctionParameterInterface => ({
          name: param.name,
          type: 'string',
          location: FunctionParameterParameterLocation.LOCATION_QUERY_PARAM,
        }),
      ),
    ];

    return {
      id: formId || '',
      description: formDescription || '',
      url: curlImport.url,
      methodType: curlImport.methodType,
      functionParameters,
      headers: curlImport.headers.map((header) => ({
        key: header.key,
        value: header.value,
      })),
    };
  }

  /**
   * Converts a CurlImportObject to a FunctionParameterInterface
   */
  convertCurlImportObjectToFunctionParameter(
    curlObject: CurlImportObject,
    isNested: boolean,
    location?: FunctionParameterParameterLocation,
  ): FunctionParameterInterface {
    const result: FunctionParameterInterface = {
      name: curlObject.name,
      type: curlObject.type,
    };

    if (!isNested) {
      result.location = location;
    }

    if (curlObject.properties && curlObject.properties.length > 0) {
      result.properties = curlObject.properties.map((prop) =>
        this.convertCurlImportObjectToFunctionParameter(prop, true, location),
      );
    }

    if (curlObject.item) {
      result.items = this.convertCurlImportArrayItemToFunctionParameter(curlObject.item);
    }

    return result;
  }

  /**
   * Converts a CurlImportArrayItem to a FunctionParameterInterface
   */
  convertCurlImportArrayItemToFunctionParameter(arrayItem: CurlImportArrayItem): FunctionParameterInterface {
    const result: FunctionParameterInterface = {
      description: arrayItem.description || '',
      type: arrayItem.type || 'string',
    };

    if (arrayItem.properties && arrayItem.properties.length > 0) {
      result.properties = arrayItem.properties.map((prop) =>
        this.convertCurlImportObjectToFunctionParameter(prop, true, arrayItem.location),
      );
    }

    if (arrayItem.item) {
      result.items = this.convertCurlImportArrayItemToFunctionParameter(arrayItem.item);
    }

    return result;
  }

  /**
   * Creates a curl command from the tool and copies it to the clipboard
   */
  functionToCurlCommand(formValue: FunctionInterface): string {
    let curlCommand = `curl -X ${formValue.methodType} `;

    if (formValue.headers && formValue.headers.length > 0) {
      formValue.headers.forEach((header: FunctionHeaderInterface) => {
        if (header.key && header.value) {
          curlCommand += `-H '${header.key}: ${header.value}' `;
        }
      });
    }

    const requestBodyArgs: Record<string, unknown> = {};
    const urlQueryArgs: Record<string, string> = {};

    const getDefaultValue = (param: FunctionParameterInterface): unknown => {
      switch (param.type) {
        case 'integer':
          return 0;
        case 'number':
          return 0;
        case 'boolean':
          return false;
        case 'array':
          if (param.items) {
            return [getDefaultValue(param.items)];
          }
          return [];
        case 'object':
          if (param.properties && Array.isArray(param.properties)) {
            const obj: Record<string, unknown> = {};
            param.properties.forEach((prop: FunctionParameterInterface) => {
              if (!prop.name) return;
              obj[prop.name] = getDefaultValue(prop);
            });
            return obj;
          }
          return {};
        case 'string':
        default:
          return '';
      }
    };

    if (formValue.functionParameters && formValue.functionParameters.length > 0) {
      formValue.functionParameters.forEach((param: FunctionParameterInterface) => {
        if (!param.name) return;
        if (param.location === FunctionParameterParameterLocation.LOCATION_QUERY_PARAM) {
          urlQueryArgs[param.name] = '';
        } else {
          requestBodyArgs[param.name] = getDefaultValue(param);
        }
      });
    }

    if (Object.keys(requestBodyArgs).length > 0) {
      curlCommand += `-d '${JSON.stringify(requestBodyArgs)}' `;
    }

    let url = formValue.url || '';
    if (Object.keys(urlQueryArgs).length > 0) {
      const searchParams = new URLSearchParams(urlQueryArgs).toString();
      url += `?${searchParams}`;
    }
    curlCommand += `'${url}'`;

    return curlCommand;
  }
}
